import { Injectable, signal } from '@angular/core';

export type ToastrType = 'success' | 'error' | 'warn' | 'info' | 'secondary' | 'contrast';

export interface ToastrData {
  type: ToastrType;
  summary?: string;
  detail?: string;
}

export interface Toastr extends ToastrData {
  id: string;
}

const TOASTR_DURATION_MS = 10000;

const DEFAULT_SUMMARIES: Record<ToastrType, string> = {
  success: 'Success',
  error: 'Error',
  warn: 'Warning',
  info: 'Information',
  secondary: 'Notice',
  contrast: 'Alert',
};

const DEFAULT_DETAILS: Record<ToastrType, string> = {
  success: 'Operation completed successfully.',
  error: 'Something went wrong.',
  warn: 'Please check the details.',
  info: 'Something you should know.',
  secondary: 'Secondary message.',
  contrast: 'Contrast styled notification.',
};

@Injectable({
  providedIn: 'root',
})
export class ToastrService {
  private toastrs = signal<Toastr[]>([]);
  private nextId = 0;
  private timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  readonly toastrsReadonly = this.toastrs.asReadonly();

  showToastr(data: ToastrData): string {
    const { type, summary, detail } = data;
    const id = `toastr-${++this.nextId}`;
    const toastr: Toastr = {
      id,
      type,
      summary: summary ?? DEFAULT_SUMMARIES[type],
      detail: detail ?? DEFAULT_DETAILS[type],
    };
    this.toastrs.update(list => [...list, toastr]);

    const timeoutId = setTimeout(() => this.dismiss(id), TOASTR_DURATION_MS);
    this.timeouts.set(id, timeoutId);
    return id;
  }

  dismiss(id: string): void {
    const timeoutId = this.timeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(id);
    }
    this.toastrs.update(list => list.filter(t => t.id !== id));
  }
}